import { includes } from 'lodash';
import { RefObject } from 'react';
import { Control } from 'react-hook-form';

import { FormLayoutItem, FormLayoutRow } from '@/components/layout/FormLayout';
import {
  PrimaryClientDropdownMultiple,
  PrimaryClientDropdownMultipleFormShape,
  PrimaryClientDropdownSingleFormShape,
  PrimaryClientTypeaheadDropdownSingle,
} from '@/modules/entities/inputs/PrimaryClientDropdown/PrimaryClientDropdown';
import { getGrantorOptionsFromData } from '@/modules/entities/inputs/PrimaryClientDropdown/PrimaryClientDropdown.utils';
import {
  GrantorsFieldSubformsNamespaceType,
  GrantorsFieldSubformValuesType,
  GrantorsFieldVariantType,
} from '@/modules/entities/principals/GrantorsField/GrantorsField.types';
import { useHouseholdDetailsContext } from '@/modules/household/contexts/householdDetails.context';

interface GrantorsFieldProps<
  T extends
    | PrimaryClientDropdownSingleFormShape
    | PrimaryClientDropdownMultipleFormShape,
> {
  control: Control<T>;
  variant: GrantorsFieldVariantType;
  subformNamespace: GrantorsFieldSubformsNamespaceType;
  subformValues: GrantorsFieldSubformValuesType;
  isCharitableEntity: boolean;
  shouldGroupDuplicates?: boolean;
  duplicatesRef?: RefObject<HTMLDivElement | null>;
}

/**
 * The Grantors fields for the Basic Information subform.
 */
export function GrantorsField<
  T extends
    | PrimaryClientDropdownSingleFormShape
    | PrimaryClientDropdownMultipleFormShape,
>({
  control,
  variant,
  subformNamespace,
  subformValues,
  isCharitableEntity,
  shouldGroupDuplicates,
  duplicatesRef,
}: GrantorsFieldProps<T>) {
  const { possibleGrantors } = useHouseholdDetailsContext();

  const isSingleGrantor = variant === 'SingleGrantor';
  const isMultiGrantor = !includes<GrantorsFieldVariantType>(
    ['SingleGrantor'],
    variant
  );

  // we specifically want to include non-client grantors here
  const grantorOptions = getGrantorOptionsFromData(
    possibleGrantors || [],
    subformValues
  );

  return (
    <>
      {isMultiGrantor && (
        <FormLayoutRow>
          <FormLayoutItem width={12}>
            <PrimaryClientDropdownMultiple<PrimaryClientDropdownMultipleFormShape>
              // @ts-ignore -- The problem here is that the "defaultValues"
              // within the nested form control state object is not expected to
              // be undefined. But EntitySubforms.tsx sets up the useForm hook
              // where defaultValues is a Partial<SubformsCombinedType> type.
              control={control}
              fieldName={`${subformNamespace}.grantors`}
              validationFieldName={`${subformNamespace}._grantorAttributionSum`}
              options={grantorOptions}
              isDonors={isCharitableEntity}
              variant={
                variant === 'MultipleGrantorWithPercent'
                  ? 'grantorAndPercent'
                  : 'grantorOnly'
              }
              shouldGroupDuplicates={shouldGroupDuplicates}
              duplicatesRef={duplicatesRef}
            />
          </FormLayoutItem>
        </FormLayoutRow>
      )}
      {isSingleGrantor && (
        <FormLayoutRow>
          <FormLayoutItem width={12}>
            <PrimaryClientTypeaheadDropdownSingle<T>
              control={control}
              namespace={subformNamespace}
              options={grantorOptions}
              // undefined is the non-isCharitableEntity case because we want to use the default handling
              labelOverride={isCharitableEntity ? 'Donor' : undefined}
            />
          </FormLayoutItem>
        </FormLayoutRow>
      )}
    </>
  );
}
